<template>
  <div>
    <BeachClubBookingsFilters :isLoading="isLoading" :initialFilters="initialFilters" @search="search" />
    <hr>
    <b-table
      responsive
      small
      show-empty
      :items="rows"
      :fields="fields"
      :busy="isLoading"
      primary-key="id"
      empty-text="No se encontraron reservas con los filtros seleccionados."
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle" /> <strong>Cargando reservas</strong>
        </div>
      </template>

      <template #cell(folio)="row">
        <router-link
          :to="{ name: 'beach-club-booking-detail', params: { id: row.item.id }, query: listQuery }"
          class="font-weight-bolder text-nowrap"
        >{{ row.item.folio }}</router-link>
      </template>

      <template #cell(housingnumber)="row">
        <div class="font-weight-bolder">{{ row.item.housingnumber }}</div>
        <small class="text-muted">{{ row.item.ownerName }}</small>
      </template>

      <template #cell(visitDate)="row">
        <span class="text-nowrap">{{ formatDay(row.item.visitDate) }}</span>
      </template>

      <template #cell(people)="row">
        <span class="text-nowrap">
          <strong>{{ row.item.adults }}</strong> adultos · <strong>{{ row.item.children }}</strong> menores · <strong>{{ row.item.infants }}</strong> infantes
        </span>
      </template>

      <template #cell(total)="row">
        <span class="text-nowrap">{{ formatMoney(row.item.total) }}</span>
      </template>

      <template #cell(statusname)="row">
        <b-badge pill :class="'bg-' + statusVariant(row.item.status)">{{ row.item.statusname }}</b-badge>
      </template>

      <template #cell(createDate)="row">
        <span class="text-nowrap">{{ formatDateTime(row.item.createDate) }}</span>
      </template>
    </b-table>

    <PaginationTable
      v-if="totalRows > perPage"
      :key="searchCount"
      :currentPage="currentPage"
      :totalRows="totalRows"
      :perPage="perPage"
      @change-page="changePage"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { showAlertMessage } from '@/helpers/helpers'
import { formatXcalacocoDay, formatXcalacocoDateTime, xcalacocoStatusVariant } from '@/helpers/xcalacocoBookingsHelper'
import { formatBeachClubMoney } from '@/helpers/beachClubBookingsHelper'
import { beachClubBookingsFields } from '@/data/beachClubBookings'
import PaginationTable from '@/modules/fivesClub/components/catalogs/ownersBookings/list/PaginationTable'
import BeachClubBookingsFilters from '@/modules/fivesClub/components/catalogs/ownersBookings/beachClub/BeachClubBookingsFilters'

const FALLBACK_MESSAGE = 'Ocurrió un error al procesar la solicitud.'
const BAD_RESPONSE_MESSAGE = 'No se pudo leer la respuesta del servidor. Intente de nuevo.'
const FILTER_KEYS = ['status', 'folio', 'housing', 'owner', 'startDate', 'endDate']
const PER_PAGE = 20

// The query only belongs to this list when it carries tab=beachclub; the Xcalacoco tab shares the
// `status`, `startDate` and `endDate` keys and its leftovers must not become Beach Club filters.
const isOwnQuery = query => query.tab === 'beachclub'

// Rebuilds the last search from the query the detail view sends back. A bare `?status` means "Todos"
function filtersFromQuery(query) {
  if (!isOwnQuery(query) || !FILTER_KEYS.some(key => key in query)) return { status: '1' }
  const filters = {}
  FILTER_KEYS.forEach(key => {
    if (key === 'status' && key in query) filters.status = query.status || null
    else if (query[key]) filters[key] = query[key]
  })
  return filters
}

function pageFromQuery(query) {
  const page = isOwnQuery(query) ? parseInt(query.page, 10) : 0
  return page >= 1 ? page : 1
}

export default {
  components: {
    PaginationTable,
    BeachClubBookingsFilters,
  },
  data() {
    return {
      initialFilters: filtersFromQuery(this.$route.query),
      rows: [],
      totalRows: 0,
      fields: beachClubBookingsFields,
      lastFilters: { status: '1' },
      listQuery: { tab: 'beachclub' },
      isLoading: false,
      currentPage: pageFromQuery(this.$route.query),
      perPage: PER_PAGE,
      searchCount: 0,
      requestCount: 0,
    }
  },
  async created() {
    this.lastFilters = this.initialFilters
    await this.load()
  },
  methods: {
    ...mapActions('fivesClubCatalogs', ['fetchBeachClubBookings']),

    search(filters) {
      this.lastFilters = filters
      this.currentPage = 1
      this.searchCount += 1
      return this.load()
    },
    changePage(page) {
      this.currentPage = page
      return this.load()
    },
    async load() {
      const filters = this.lastFilters
      const page = this.currentPage
      this.listQuery = { ...filters, page, tab: 'beachclub' }
      // The URL holds the search and the page so browser Back from the detail view restores them
      this.$router.replace({ query: this.listQuery }).catch(() => {})
      this.requestCount += 1
      const requestId = this.requestCount
      this.isLoading = true
      const result = await this.fetchBeachClubBookings({ ...filters, page })
      // A newer search or page change is in flight; its answer is the one to show
      if (requestId !== this.requestCount) return
      this.isLoading = false
      if (result.ok && result.data && Array.isArray(result.data.rows)) {
        const { rows, total } = result.data
        this.rows = rows
        this.totalRows = Number(total) || 0
        // The requested page vanished (e.g. its last booking was confirmed): jump back to the last real one
        const lastPage = Math.ceil(this.totalRows / PER_PAGE)
        if (!rows.length && lastPage > 0 && lastPage < page) {
          this.currentPage = lastPage
          this.searchCount += 1 // remounts the pager so it shows the corrected page
          await this.load()
        }
        return
      }
      this.rows = []
      this.totalRows = 0
      // A 200 without { rows } must not read as "no results"
      const message = result.ok ? BAD_RESPONSE_MESSAGE : result.message
      showAlertMessage('Atención', 'InfoIcon', message || FALLBACK_MESSAGE, 'warning', 5000)
    },
    formatDay: formatXcalacocoDay,
    formatDateTime: formatXcalacocoDateTime,
    formatMoney: formatBeachClubMoney,
    statusVariant: xcalacocoStatusVariant,
  },
}
</script>
